/* eslint-disable max-lines */
import { Box, Button, Icon, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import React from 'react';

import { RequireRole } from 'app/components/require-role';
import { SeamRequestLink } from 'app/components/seam-request-link';
import { TabPanel } from 'app/components/tab-panel';
import { CompareStatus, parseCompareStatus } from 'app/constants/compare-status';
import { Role } from 'app/constants/role';
import { SeamStatus } from 'app/constants/seam-status';
import { TimingConstants } from 'app/constants/timing-constants';
import { MidTuple } from 'app/model/mid-tuple';
import { SeamCompareResults } from 'app/model/request/seam-compare-results';
import { SeamRequest } from 'app/model/seam-request';
import { seamRequestService } from 'app/services/seam-request.service';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';
import { Formatters } from 'app/utils/formatters';

import { RequestListGrid } from '../../request-list-grid';

interface PatternOverlapCheckerProps {
    request: SeamRequest;
}

interface PatternOverlapCheckerState {
    tabValue: number;
    result?: SeamCompareResults;
    relatedRequests?: SeamRequest[];
    otherRequest?: SeamRequest;
}

const columns: Array<GridColDef<MidTuple>> = [
    {
        headerName: 'MID',
        field: 'mid',
        width: 160,
    },
    {
        headerName: 'Descriptor',
        field: 'merchdesc',
        width: 300,
    },
    {
        headerName: 'MCC Name',
        field: 'mccName',
        width: 200,
    },
    {
        headerName: 'MCC',
        field: 'mcc',
        width: 88,
        disableColumnMenu: true,
    },
    {
        headerName: 'Trxs',
        field: 'trxCount',
        width: 85,
        disableColumnMenu: true,
        valueFormatter: DataGridFormatters.numberLocaleFormatter,
    },
    {
        headerName: 'Spend',
        field: 'totalSpend',
        width: 150,
        valueFormatter: DataGridFormatters.numberLocaleFormatter,
    },
    { headerName: 'Channel', field: 'channel', width: 109, disableColumnMenu: true },
    {
        headerName: 'Store Id',
        field: 'storeId',
        width: 110,
        disableColumnMenu: true,
    },
    {
        headerName: 'Store Name',
        field: 'storeName',
        width: 120,
        disableColumnMenu: true,
    },
    {
        headerName: 'Brand Id',
        field: 'brandId',
        width: 110,
        disableColumnMenu: true,
    },
    {
        headerName: 'Brand Name',
        field: 'brandName',
        width: 120,
        disableColumnMenu: true,
    },
    {
        headerName: 'City',
        field: 'city',
        width: 150,
    },
    {
        headerName: 'State',
        field: 'state',
        width: 90,
        disableColumnMenu: true,
    },
    {
        headerName: 'Zip',
        field: 'zip',
        width: 88,
        disableColumnMenu: true,
    },
    {
        headerName: 'Country',
        field: 'country',
        width: 107,
        disableColumnMenu: true,
    },
    { headerName: 'Store Num', field: 'storeNum', width: 125, disableColumnMenu: true },
    { headerName: 'Cor Pat id', field: 'correctionPatId', width: 150, disableColumnMenu: true },
    { headerName: 'Chan Method', field: 'channelMethod', width: 150, disableColumnMenu: true },
    { headerName: 'Chan Pat Id', field: 'channelPatId', width: 150, disableColumnMenu: true },
    {
        headerName: 'Store Match Method',
        field: 'storeMatchMethod',
        width: 200,
        disableColumnMenu: true,
    },
    {
        headerName: 'Store Match Source',
        field: 'storeMatchSource',
        width: 200,
        disableColumnMenu: true,
    },
    { headerName: 'Store Num Pat Id', field: 'storeNumPatId', width: 180, disableColumnMenu: true },
];

export class PatternOverlapChecker extends React.Component<PatternOverlapCheckerProps, PatternOverlapCheckerState> {
    private refreshTimer?: number;

    public constructor(props: PatternOverlapCheckerProps | Readonly<PatternOverlapCheckerProps>) {
        super(props);

        this.state = {
            tabValue: 0,
        };
    }

    public componentDidMount(): void {
        seamRequestService
            .listAll({
                queryFilters: {
                    store: this.props.request.store.id,
                    notStatus: [SeamStatus.CLOSED, SeamStatus.CANCELLED],
                },
            })
            .subscribe((requests) => {
                this.setState({
                    relatedRequests: requests.filter((request) => {
                        if (request.id === this.props.request.id) {
                            return false;
                        }

                        return request.correction !== this.props.request.correction;
                    }),
                });
            });

        this.refreshTimer = window.setInterval(() => this.refreshResults(), TimingConstants.LIST_REFRESH_INTERVAL);
    }

    public componentWillUnmount(): void {
        if (this.refreshTimer) {
            window.clearInterval(this.refreshTimer);
        }
    }

    public render(): React.ReactNode {
        const extraCols: GridColDef[] = [
            {
                field: '',
                headerName: 'Action',
                width: 300,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <>
                        <Button
                            onClick={() => this.comparePattern(params.row as SeamRequest, false)}
                            variant='outlined'
                            size='small'
                        >
                            Inspect
                        </Button>
                        <RequireRole roles={Role.SEAM_MANAGER}>
                            <Button
                                onClick={() => this.comparePattern(params.row as SeamRequest, true)}
                                variant='outlined'
                                size='small'
                            >
                                Inspect with Deployed
                            </Button>
                        </RequireRole>
                    </>
                ),
                sortable: false,
            },
        ];

        const result = this.state.result;
        const fromId = this.props.request.id;
        const toId = this.state.otherRequest?.id || '';

        return (
            <Box>
                <Typography variant='h4'>Pattern Overlap Checker</Typography>
                <Tabs value={this.state.tabValue} onChange={(evt, value) => this.setState({ tabValue: Number(value) })}>
                    <Tab label='Related Requests' />
                    <Tab label='Overlap Results' disabled={undefined === this.state.otherRequest} />
                </Tabs>
                <TabPanel value={this.state.tabValue} index={0}>
                    <Typography variant='body2'>
                        The table below shows all requests qualifying for overlap check. Click the{' '}
                        <strong>Inspect</strong> button to run the overlap check query.
                    </Typography>
                    <RequestListGrid
                        rows={this.state.relatedRequests || []}
                        startColumns={extraCols}
                        initialState={{ pagination: { paginationModel: { page: 0, pageSize: 50 } } }}
                        disableRowSelectionOnClick
                        density='compact'
                        pagination
                        autoHeight
                        loading={undefined === this.state.relatedRequests}
                    />
                </TabPanel>
                <TabPanel value={this.state.tabValue} index={1}>
                    <Typography variant='body1'>
                        Overlap between&nbsp;
                        <SeamRequestLink id={fromId} /> and&nbsp;
                        <SeamRequestLink id={toId} />. Once query completes, the results below will list all MID Tuples
                        that overlap between the two patterns. Only the first 250 overlapping records will be displayed,
                        and sorting only applies to those displayed records.
                    </Typography>
                    {this.renderResults(result)}
                </TabPanel>
            </Box>
        );
    }

    private comparePattern(otherRequest: SeamRequest, withLegacy: boolean) {
        this.setState({
            otherRequest,
            tabValue: 1,
            result: undefined,
        });

        this.doCompare(otherRequest, false, withLegacy);
    }

    private doCompare(otherRequest: SeamRequest, reRun = false, withLegacy = false): void {
        seamRequestService.compare(this.props.request, otherRequest, reRun, withLegacy).subscribe((result) => {
            this.setState({
                result,
            });
        });
    }

    private refreshResults() {
        if (this.state.result?.status === CompareStatus.PENDING && this.state.otherRequest) {
            this.doCompare(this.state.otherRequest);
        }
    }

    private reRunRequest(withLegacy: boolean) {
        if (this.state.otherRequest) {
            this.setState({ result: undefined });
            this.doCompare(this.state.otherRequest, true, withLegacy);
        }
    }

    private renderResults(result?: SeamCompareResults) {
        if (!result) {
            return undefined;
        }

        const rows = result.results.hits || [];

        return (
            <React.Fragment>
                <hr />
                <Box display='flex'>
                    <Box>
                        <Typography>
                            Status: <strong>{parseCompareStatus(result.status)}</strong>
                        </Typography>
                        <Typography>
                            Date: <strong>{Formatters.dateTime(result.dateCreated)}</strong>
                        </Typography>
                        <Typography>
                            Requester: <strong>{Formatters.userDisplayName(result.userCreated)}</strong>
                        </Typography>
                        {result.message ? (
                            <Typography>
                                Message: <strong>{result.message}</strong>
                            </Typography>
                        ) : (
                            ''
                        )}
                    </Box>
                    <Box display='flex' flexGrow={1} flexDirection='row-reverse'>
                        <span>
                            <Tooltip title='download' sx={{ mr: 1 }}>
                                <IconButton
                                    aria-label='download'
                                    color='secondary'
                                    href={result.signedUrl}
                                    disabled={!result.signedUrl}
                                    target='_blank'
                                    download
                                >
                                    <Icon>download</Icon>
                                </IconButton>
                            </Tooltip>
                            <Button
                                variant='outlined'
                                color='secondary'
                                onClick={() => this.reRunRequest(false)}
                                disabled={result.status === CompareStatus.PENDING}
                            >
                                Re-Run
                            </Button>
                            <RequireRole roles={Role.SEAM_MANAGER}>
                                <Button
                                    variant='outlined'
                                    color='secondary'
                                    onClick={() => this.reRunRequest(true)}
                                    disabled={result.status === CompareStatus.PENDING}
                                >
                                    Re-Run with Deployed
                                </Button>
                            </RequireRole>
                        </span>
                    </Box>
                </Box>

                {result.status === CompareStatus.COMPLETED ? (
                    <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                        <DataGridPro
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        storeId: false,
                                        storeName: false,
                                        correctionPatId: false,
                                        channelMethod: false,
                                        channelPatId: false,
                                        storeMatchMethod: false,
                                        storeMatchSource: false,
                                        storeNumPatId: false,
                                    },
                                },
                                pagination: { paginationModel: { page: 0, pageSize: 50 } },
                            }}
                            columns={columns}
                            rows={rows}
                            getRowId={(row) => `${row.mtid}-${row.storeId}`}
                            disableRowSelectionOnClick
                            density='compact'
                            pagination
                            autoHeight
                        />
                    </Box>
                ) : (
                    <Typography color='textSecondary'>(Page will auto refresh to check for updates)</Typography>
                )}
            </React.Fragment>
        );
    }
}
